import UndoIcon from "@mui/icons-material/Undo";
import { LoadingButton } from "@mui/lab";
import { Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack, Typography } from "@mui/material";
import { useState } from "react";
import { useRefresh } from "react-admin";
import { useQueryClient } from "react-query";
import { getAuthenticatedBackendClient } from "../api/backend.api";
import { currentUserRoles, grants, isAutovioStaff } from "../backoffice.access_control";
import { ApiError, type PrepaidRefund } from "../generated/backendClient";
import { useDialog } from "../hooks/useDialog";
import { DialogCloseButton } from "../misc/DialogCloseButton";
import { Money } from "../misc/Money";
import type { Invoice } from "../providers/invoicesProvider";
import { moneyToCents } from "../utils/moneyToCents";
import { LocalizedError } from "../utils/LocalizedError";

export function RefundPrepaidCreditsButton(props: { invoice: Invoice; prepaidCredits: number }) {
  const { invoice, prepaidCredits } = props;
  const { dialogProps, openDialog, closeDialog } = useDialog();
  const [refund, setRefund] = useState<PrepaidRefund>();
  const [loading, setLoading] = useState(false);
  const refresh = useRefresh();
  const queryClient = useQueryClient();
  if (!grants.has("PrepaidCredits:refund") && !isAutovioStaff(currentUserRoles)) {
    return null;
  }

  const loadRefund = async () => {
    setLoading(true);
    await runSafely(
      async () => {
        const client = await getAuthenticatedBackendClient();
        setRefund(
          await client.invoice.invoiceRefundPrepaidCreditsRetrieve({ id: invoice.id }).catch(localizeRefundError),
        );
        openDialog();
      },
      { errorMessage: "Die Rückerstattung konnte nicht vorbereitet werden.", successMessage: "" },
    );
    setLoading(false);
  };

  const executeRefund = async () => {
    if (!refund || loading) return;
    setLoading(true);
    await runSafely(
      async () => {
        const client = await getAuthenticatedBackendClient();
        await client.invoice
          .invoiceRefundPrepaidCreditsCreate({
            id: invoice.id,
            requestBody: { refund_amount: refund.refund_amount },
          })
          .catch(localizeRefundError);
        closeDialog();
        refresh();
        void queryClient.invalidateQueries(["backendUser"]);
        window.setTimeout(refresh, 5000);
      },
      {
        successMessage:
          refund.refund_method === "EXTERNAL_REFUND"
            ? "Die Rückerstattung wurde dokumentiert."
            : "Die Rückerstattung wurde veranlasst.",
        errorMessage: "Die Rückerstattung konnte nicht ausgeführt werden.",
      },
    );
    setLoading(false);
  };

  return (
    <>
      <LoadingButton
        size="small"
        startIcon={<UndoIcon />}
        loading={loading}
        disabled={
          prepaidCredits <= 0 ||
          !["PAID", "PARTIALLY_REFUNDED"].includes(invoice.status) ||
          invoice.total <= (invoice.refunded_amount ?? 0)
        }
        onClick={loadRefund}
      >
        Rückerstatten
      </LoadingButton>
      <Dialog {...dialogProps} onClose={loading ? undefined : closeDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Guthaben zurückerstatten</DialogTitle>
        <DialogCloseButton onClick={closeDialog} disabled={loading} />
        <DialogContent>
          {refund && (
            <Stack spacing={2}>
              <Typography>
                Von Quittung {invoice.nr} können <Money cents={moneyToCents(refund.refund_amount)} /> erstattet werden.
                Bereits verbrauchtes oder erstattetes Guthaben ist darin nicht enthalten.
              </Typography>
              <Alert severity="info">
                {refund.refund_method === "EXTERNAL_REFUND" &&
                  "Die Fahrschule zahlt den Betrag selbst bar oder per Überweisung zurück. Hier wird die Rückerstattung dokumentiert und das Guthaben entsprechend verringert."}
                {refund.refund_method === "STRIPE_REFUND" &&
                  "Der Betrag wird über Stripe auf das ursprüngliche Zahlungsmittel zurückerstattet."}
                {refund.refund_method === "MANUAL_REFUND" &&
                  "Der Betrag kann nicht mehr direkt über Stripe erstattet werden. Das Guthaben wird der Fahrschule zur Rückzahlung bereitgestellt. Sie erhält eine E-Mail und muss den Betrag selbst an den Fahrschüler überweisen."}
              </Alert>
              <Typography>Ein Beleg zur Rückerstattung wird erstellt.</Typography>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog} disabled={loading}>
            Abbrechen
          </Button>
          <LoadingButton variant="contained" onClick={executeRefund} loading={loading} disabled={!refund}>
            {refund?.refund_method === "EXTERNAL_REFUND" ? "Rückerstattung dokumentieren" : "Jetzt rückerstatten"}
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </>
  );
}

function localizeRefundError(error: unknown): never {
  if (error instanceof ApiError && error.status === 400) {
    const message = Array.isArray(error.body) ? error.body.join(" ") : error.body?.detail;
    if (typeof message === "string") {
      throw new LocalizedError(message);
    }
  }
  throw error;
}
